import type { HttpMethod } from './Http.types';

export enum ServiceAuthMethod {
  COGNITO = 'COGNITO',
  HMAC = 'HMAC',
  NONE = 'NONE'
}

export enum ServiceVersion {
  ALPHA = 'ALPHA', // No Unit Tests
  BETA = 'BETA', // Maybe Unit Tests
  V1 = 'V1', // Unit Tests Mandatory
  V10 = 'V10',
  V2 = 'V2',
  V3 = 'V3',
  V4 = 'V4',
  V5 = 'V5',
  V6 = 'V6',
  V7 = 'V7',
  V8 = 'V8',
  V9 = 'V9'
}

export interface IIdSet extends Iterable<number> {
  [Symbol.iterator](): Iterator<number>;
  add(id: number): void;
  forEach(callback: (id: number) => void): void;
  getHighest(): number | null;
  getLowest(): number | null;
  remove(id: number): void;
  size(): number;
  toCompactString(): string;
  toString(): string;
}

export type Id = number; // A single ID, eg: 123

export type IdRange = number | number[] | IIdSet; // A range of IDs, eg: [123, 456]

/** These are the types that can be used in path params. */
export type PathParamsTypes = number | number[];

export type PathParamsType = Record<string, PathParamsTypes>;

/** These are the types that can be used in query params. */
export type QueryParamsTypes = string | string[] | number | number[] | boolean;

export type QueryParamsType = Record<string, QueryParamsTypes | undefined>;

/** Do not change this! Ever! */
export const ENDPOINT_HMAC_NAME = 'hmac';
export const ENDPOINT_HMAC_PATH = 'hmac';
export const ENDPOINT_HMAC_VERSION = ServiceVersion.V1;

/**
 * NAME must be a camelCase identifier (it flows into the CDK construct ID, the Lambda handler name, and the
 * generated SDK method) — only PATH is the hyphenated URL fragment. Do not re-align them: a hyphenated name
 * produces an invalid (non-PascalCase) construct-ID fragment at deploy time.
 */
export const ENDPOINT_CLIENT_CONFIG_NAME = 'clientConfig';
export const ENDPOINT_CLIENT_CONFIG_PATH = 'client-config';
export const ENDPOINT_CLIENT_CONFIG_VERSION = ServiceVersion.V1;

export interface HmacResponse {
  hmacToken: string;
  timestamp: string;
}

/** Client-facing settings for one IoT connection ref. */
export interface ClientConfigIot {
  identityPoolId: string;
  iotEndpoint: string;
  /**
   * Custom MQTT-over-WebSocket hostname, when the deployment fronts the IoT data endpoint with one. Optional:
   * a Lambda deployed before this var existed still serves a valid config, and clients fall back to
   * `iotEndpoint`.
   */
  mqttEndpoint?: string;
  region: string;
  topicPrefix: string;
}

/** Backend-served, per-environment client config. Lean: only capabilities/keys with data are serialized. */
export interface ClientConfigResponse {
  /** Present only when the service exposes >= 1 IoT connection. Keyed by connection ref (original casing, eg: 'ConvoyIot'). */
  iot?: { [connectionRef: string]: ClientConfigIot };
}

/** ---- ServiceManifest ---- */

export interface ServiceManifestV2Map {
  [packageName: string]: ServiceManifestV2;
}

export interface ServiceManifestV2 {
  bucketName?: string;
  endpoints: ServiceManifestEndpointV2[];
  forceHmac?: boolean;
  hasCognitoAuth: boolean;
  hasHmacAuth: boolean;
  manifestVersion: number;
  packageName: string;
  queuedHandlers?: ServiceManifestQueuedHandlerV2[]; // omitted when empty (like scheduledHandlers)
  scheduledHandlers?: ServiceManifestScheduledHandlerV2[];
  /** The package's `blufin.name` (eg: `auth`) — the `service` segment of the Lambda functionName contract (design §5.2). Absent on manifests written before Plan 1 of the firehose pipeline. */
  serviceBaseName?: string;
  uploadedAtUtc: string;
  version: string; // eg: 2021-08-31T14:00:00Z
}

export interface ServiceManifestEndpointV2 {
  authorization: ServiceAuthMethod;
  bucketKey?: string;
  callbackAsync?: boolean;
  dynamoDbMappingKeys?: string[];
  envVarKeys?: string[];
  grafanaMappingKey?: string;
  httpMethod: HttpMethod;
  iotMappingKeys?: string[];
  localPathToZipFile?: string;
  mysqlDatabaseMappingKeys?: string[];
  name: string;
  path: string;
  postgresDatabaseMappingKeys?: string[];
  queueMappingKeys?: string[]; // from require.queues, consumer base names
  requiresCloudfrontDistribution?: boolean;
  requiresS3Bucket?: boolean;
  s3VersionId?: string;
  ssmParameterPaths?: string[];
  ssmParameterWrite?: boolean;
  stsAssumeRole?: string[];
  /**
   * Mirrors `ServiceEndpoint.telemetrySink` (`@blufin/api`) — routes this endpoint's Lambda logs into the
   * fixed telemetry SINK log group `/blufin/telemetry/{env}/{logs|metrics}` instead of the generic 7-part
   * auto-name. The name is a function of (sink, env) only, so `ServiceStackV2` creates the group ONCE per
   * sink per stack and every endpoint declaring that sink shares it. A given sink may be declared by
   * endpoints of exactly ONE service package (enforced by `validateServices`).
   */
  telemetrySink?: 'logs' | 'metrics';
  version: ServiceVersion;
}

/**
 * A scheduled handler (periodic Lambda) entry in the service manifest. Parallels
 * `ServiceManifestEndpointV2` but has no HTTP fields — scheduled handlers are triggered by EventBridge
 * Scheduler, not API Gateway. The `*MappingKeys` fields carry the same `require`-derived connection refs
 * the CDK stack uses to grant IAM + inject env vars.
 */
export interface ServiceManifestScheduledHandlerV2 {
  bucketKey?: string;
  dynamoDbMappingKeys?: string[];
  envVarKeys?: string[];
  iotMappingKeys?: string[];
  localPathToZipFile?: string;
  mysqlDatabaseMappingKeys?: string[];
  name: string; // base name, eg: "overdueTaskSweeper"
  postgresDatabaseMappingKeys?: string[];
  queueMappingKeys?: string[]; // from require.queues, consumer base names
  requiresCloudfrontDistribution?: boolean;
  requiresS3Bucket?: boolean;
  s3VersionId?: string;
  ssmParameterPaths?: string[];
  ssmParameterWrite?: boolean;
  stsAssumeRole?: string[];
  version: ServiceVersion;
}

/**
 * A queued handler (SQS-consumer Lambda) entry in the service manifest. Parallels
 * `ServiceManifestScheduledHandlerV2` but is triggered by an SQS event source mapping. `name` is the
 * consumer's base name, which is also the handler's `queue` literal.
 */
export interface ServiceManifestQueuedHandlerV2 {
  batchSize: number; // resolved default applied at build time (1)
  bucketKey?: string;
  dynamoDbMappingKeys?: string[];
  envVarKeys?: string[];
  iotMappingKeys?: string[];
  localPathToZipFile?: string;
  maxConcurrency?: number; // absent = uncapped
  maxReceiveCount: number; // resolved default 3
  mysqlDatabaseMappingKeys?: string[];
  name: string; // base name == the handler's `queue` literal, eg: "mergeFactsRecheck"
  postgresDatabaseMappingKeys?: string[];
  queueMappingKeys?: string[]; // the consumer's own require.queues (self re-enqueue)
  requiresCloudfrontDistribution?: boolean;
  requiresS3Bucket?: boolean;
  s3VersionId?: string;
  ssmParameterPaths?: string[];
  ssmParameterWrite?: boolean;
  stsAssumeRole?: string[];
  version: ServiceVersion;
}
